import { IkeDelayedTask } from '@ikegps/core-shared';
import { action, computed, observable, makeObservable } from 'mobx';
import { ImageButton } from '../../components/button/image.button';
import { Icon } from '../../components/icon';
import { MaterialIcon } from '../../components/icon/icons';
import { IMithrilStateComponent } from '../../components/mithril.component';
import { ModalService } from '../../components/modal/modal.service';
import { redDot } from '../../components/red.dot/red.dot';
import { Spinner } from '../../components/spinner/spinner';
import { addStyles } from '../../components/theme';
import { T, Translate } from '../../i18n';
import { m, type MithrilVNode } from '../../mithril';
import { Services } from '../../services';
import { LongRunningTaskManagerService } from './long.running.task.manager.service';
import {
	LongRunningTaskManagerState,
	type LongRunningExport,
	type LongRunningTaskDisplayStatus,
	getDisplayStatus,
} from './long.running.task.manager.state';
import { viewExportError } from '../export.notification/export.notification';
import { CSS, Style } from './long.running.task.manager.style';

addStyles(Style);

export class LongRunningTaskManager extends IMithrilStateComponent {
	state: LongRunningTaskManagerState;
	service: LongRunningTaskManagerService;

	/** Incremented every second while popover is open, to re-render elapsed times */
	@observable private tick = 0;
	@observable private isPopoverOpening = false;
	@observable private isPopoverClosing = false;
	private tickInterval: ReturnType<typeof setInterval> | null = null;
	private closeTask = new IkeDelayedTask(() => this.completeClose(), 200);

	constructor() {
		super();
		makeObservable(this);

		this.state = new LongRunningTaskManagerState();
		this.service = new LongRunningTaskManagerService(this.state);
	}

	@computed get nodes(): MithrilVNode {
		const departmentId = Services.Department.current?.objectId;
		if (!departmentId) return null;

		const departmentExportTasks = this.state
			.tasksForDepartment(departmentId)
			.filter((t) => LongRunningTaskManagerState.isExportTask(t));
		const unNotifiedCount = this.state.unNotifiedCountForDepartment(departmentId);
		const hasUnactioned = this.state.hasUnactionedForDepartment(departmentId);

		let badgeView: MithrilVNode = null;
		if (unNotifiedCount > 0) {
			badgeView = this.viewBadge(unNotifiedCount);
		} else if (hasUnactioned) {
			badgeView = this.viewRedDot();
		}

		return m('div', { className: `${CSS.container}${this.state.isPulsing ? ` ${CSS.pulse}` : ''}` }, [
			ImageButton.view(MaterialIcon.FILE_DOWNLOAD, {
				onclick: this.togglePopover,
				title: T.EXPORT_MANAGER_TITLE,
				id: 'export-manager',
			}),
			badgeView,
			this.state.isPopoverOpen || this.isPopoverClosing ? this.viewPopover(departmentExportTasks, departmentId) : null,
		]);
	}

	@action.bound togglePopover(): void {
		if (this.state.isPopoverOpen && !this.isPopoverClosing) {
			this.hidePopover();
		} else {
			this.showPopover();
		}
	}

	@action.bound showPopover(): void {
		const wasOpen = this.state.isPopoverOpen;
		this.closeTask.cancel();
		this.state.isPopoverOpen = true;
		this.isPopoverClosing = false;
		this.isPopoverOpening = !wasOpen;
		this.state.isPulsing = false;
		this.state.markAllNotified();
		this.startTicking();
	}

	@action.bound hidePopover(): void {
		if (!this.state.isPopoverOpen) return;
		this.isPopoverClosing = true;
		this.isPopoverOpening = false;
		if (this.closeTask.isActive) return;
		this.closeTask.delay();
	}

	@action.bound private completeClose(): void {
		this.state.isPopoverOpen = false;
		this.isPopoverClosing = false;
		this.stopTicking();
	}

	@action.bound private startOpenTransition(): void {
		if (!this.isPopoverOpening) return;
		requestAnimationFrame(() =>
			requestAnimationFrame(
				action(() => {
					this.isPopoverOpening = false;
				})
			)
		);
	}

	override onRemove(): void {
		super.onRemove();
		this.stopTicking();
		this.closeTask.cancel();
	}

	private startTicking(): void {
		if (this.tickInterval != null) return;
		this.tickInterval = setInterval(
			action(() => {
				this.tick++;
			}),
			1000
		);
	}

	private stopTicking(): void {
		if (this.tickInterval != null) {
			clearInterval(this.tickInterval);
			this.tickInterval = null;
		}
	}

	private viewBadge(count: number): MithrilVNode {
		return m('span', { className: CSS.badge }, `${count}`);
	}

	private viewRedDot(): MithrilVNode {
		return redDot({ overlay: true });
	}

	private viewPopover(tasks: LongRunningExport[], departmentId: string): MithrilVNode {
		const hasClearable = this.state.hasClearableItemsForDepartment(departmentId);
		let className = CSS.popover;
		if (this.isPopoverOpening) className += ` ${CSS.popoverOpening}`;
		if (this.isPopoverClosing) className += ` ${CSS.popoverClosing}`;
		return m(
			'div',
			{
				className,
				oncreate: (node) => {
					Services.FocusAway.from(node.dom, this.hidePopover);
					this.startOpenTransition();
				},
			},
			[
				m('div', { className: CSS.header }, [
					m('span', { className: CSS.title }, Translate(T.EXPORT_MANAGER_TITLE)),
					m(
						'button',
						{
							className: CSS.clearButton,
							disabled: !hasClearable,
							onclick: () => this.handleClear(departmentId),
						},
						Translate(T.EXPORT_MANAGER_CLEAR)
					),
				]),
				tasks.length === 0
					? m('div', { className: CSS.empty }, Translate(T.EXPORT_MANAGER_EMPTY))
					: m(
							'div',
							{ className: CSS.table },
							tasks
								.slice(0)
								.reverse()
								.map((task) => this.viewExportRow(task))
						),
			]
		);
	}

	private async handleClear(departmentId: string): Promise<void> {
		const hasUnactioned = this.state.hasUnactionedForDepartment(departmentId);
		if (hasUnactioned) {
			const result = await ModalService.open({
				title: Translate(T.EXPORT_MANAGER_TITLE),
				content: Translate(T.EXPORT_MANAGER_CLEAR_CONFIRM),
				hasCancel: true,
			});
			if (!result.ok) return;
		}
		this.state.clearCompleted(departmentId);
	}

	private viewExportRow(task: LongRunningExport): MithrilVNode {
		const displayStatus = getDisplayStatus(task);
		const isActionable = displayStatus !== 'processing';
		const showIndicator = displayStatus !== 'processing' && !task.seen;

		return m(
			'div',
			{
				className: `${CSS.row} ${isActionable ? CSS.rowClickable : ''}`,
				onclick: isActionable ? () => this.handleAction(task, displayStatus) : undefined,
				key: task.taskId,
			},
			[
				m(
					'span',
					{ className: CSS.cellIndicator },
					showIndicator ? m('span', { className: `${CSS.unseenDot} ${this.dotColorClass(displayStatus)}` }) : null
				),
				m('span', { className: CSS.cellType }, task.exportType),
				m('span', { className: CSS.cellJob }, task.jobName),
				m('span', { className: CSS.cellStatus }, this.viewStatus(task, displayStatus)),
				m('span', { className: CSS.cellAction }, this.viewActionIcon(displayStatus)),
			]
		);
	}

	private dotColorClass(displayStatus: LongRunningTaskDisplayStatus): string {
		switch (displayStatus) {
			case 'ready':
			case 'downloaded':
				return CSS.unseenDotComplete;
			case 'warning':
				return CSS.unseenDotWarning;
			case 'error':
			case 'failed':
				return CSS.unseenDotError;
			default:
				return '';
		}
	}

	private viewStatus(
		task: LongRunningExport,
		displayStatus: LongRunningTaskDisplayStatus
	): MithrilVNode | MithrilVNode[] {
		// Reference tick to ensure MobX tracks it for re-renders
		void this.tick;

		switch (displayStatus) {
			case 'processing': {
				const elapsed = this.formatElapsed(Date.now() - task.startedAt);
				return m('span', { className: CSS.statusProcessing }, elapsed);
			}
			case 'ready':
				return m('span', { className: CSS.statusComplete }, Translate(T.EXPORT_MANAGER_STATUS_READY));
			case 'downloaded':
				return m('span', { className: CSS.statusDownloaded }, Translate(T.EXPORT_MANAGER_STATUS_DOWNLOADED));
			case 'warning':
				return m('span', { className: CSS.statusWarning }, Translate(T.EXPORT_MANAGER_STATUS_WARNING));
			case 'error':
				return m('span', { className: CSS.statusError }, Translate(T.EXPORT_MANAGER_STATUS_ERROR));
			case 'failed':
				return m('span', { className: CSS.statusError }, Translate(T.EXPORT_MANAGER_STATUS_FAILED));
		}
	}

	private viewActionIcon(displayStatus: LongRunningTaskDisplayStatus): MithrilVNode {
		switch (displayStatus) {
			case 'processing':
				return Spinner.view();
			case 'ready':
			case 'downloaded':
				return Icon.view(MaterialIcon.FILE_DOWNLOAD, { className: CSS.actionIcon });
			case 'warning':
				return Icon.view(MaterialIcon.ANNOUNCEMENT, { className: `${CSS.actionIcon} ${CSS.actionWarning}` });
			case 'error':
				return Icon.view(MaterialIcon.ANNOUNCEMENT, { className: `${CSS.actionIcon} ${CSS.actionError}` });
			case 'failed':
				return Icon.view(MaterialIcon.WARNING, { className: `${CSS.actionIcon} ${CSS.actionError}` });
		}
	}

	private handleAction(task: LongRunningExport, displayStatus: LongRunningTaskDisplayStatus): void {
		const taskUpdate: Partial<LongRunningExport> = { seen: true, actioned: true };
		this.state.updateTask(task.taskId, taskUpdate);
		switch (displayStatus) {
			case 'ready':
			case 'downloaded':
				this.service.downloadTask(task.taskId);
				break;
			case 'warning':
			case 'error':
				// eslint-disable-next-line @typescript-eslint/no-floating-promises
				this.service.showNotificationModal(task.taskId);
				this.hidePopover();
				break;
			case 'failed':
				// eslint-disable-next-line @typescript-eslint/no-floating-promises
				ModalService.open({
					title: Translate(T.NOTIFICATION_ERROR_TITLE),
					content: viewExportError(task.error || Translate(T.ERROR_EXPORT)),
				});
				break;
		}
	}

	private formatElapsed(ms: number): string {
		const seconds = Math.floor(ms / 1000);
		if (seconds < 60) return `${seconds}s`;
		const minutes = Math.floor(seconds / 60);
		return `${minutes}m ${seconds % 60}s`;
	}
}
